import React from "react";
import { Box, Alert, Text, Stack } from "@mantine/core";
import { ImageContentBlock } from "../ImageContentBlock";
import { VideoContentBlock } from "../VideoContentBlock";
import { MarkdownContentBlock } from "../MarkdownContentBlock";
import { HTMLContentBlock } from "../HTMLContentBlock";
import { IFrameContentBlock } from "../IFrameContentBlock";
import { ContentBlockErrorBoundary } from "../../generic/ContentBlockErrorBoundary";
import { ContentBlock, Venue } from "@/utils/api";
import {
  ContentBlockTypeCalendar,
  ContentBlockTypeClock,
  ContentBlockTypeCountDown,
  ContentBlockTypeCountUp,
  ContentBlockTypeMessage,
  UnsplashCollectionContentBlock,
} from "../ContentBlockTypes";
import { BlockTransitionAnimationType } from "../BlockTransitionAnimationSelector";

export interface ContentBlockRendererProps {
  contentBlock: ContentBlock;
  className?: string;
  themeConfig?: any;
  venue?: Venue;
  totalBlocks?: number;
  transitionType?: BlockTransitionAnimationType;
  transitionDuration?: number;
  onLoad?: () => void;
  onError?: (error: string) => void;
  onPlay?: () => void;
  onPause?: () => void;
  onEnded?: () => void;
}

export const ContentBlockRenderer: React.FC<ContentBlockRendererProps> = ({
  contentBlock,
  className,
  themeConfig,
  venue,
  totalBlocks,
  transitionType = "none",
  transitionDuration = 500,
  onLoad,
  onError,
  onPlay,
  onPause,
  onEnded,
}) => {
  if (!contentBlock) {
    return (
      <Box
        className={`content-block-renderer content-block-renderer--invalid ${className || ""}`}
        h="100%"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "var(--mantine-spacing-lg)",
        }}
      >
        <Stack align="center" gap="md" maw={400}>
          <Alert color="red" title="Invalid Content Block" className="content-block-renderer__error-alert">
            <Text size="sm" className="content-block-renderer__error-message" ta="center">
              No content block data provided.
            </Text>
          </Alert>
        </Stack>
      </Box>
    );
  }

  const getContentSource = (): string | null => {
    const contentType = contentBlock.contentType?.toLowerCase();

    if (contentType === "image" || contentType === "video" || contentType === "external_video" || contentType === "iframe") {
      return contentBlock.fileUrl || contentBlock.contentData || null;
    }

    if (contentType === "markdown" || contentType === "html") {
      return contentBlock.contentData || contentBlock.fileUrl || null;
    }

    return null;
  };

  const contentSource = getContentSource();

  const renderContentComponent = () => {
    const contentType = contentBlock.contentType?.toLowerCase();

    switch (contentType) {
      case "image":
        return (
          <ImageContentBlock
            imageUrl={contentSource}
            alt={contentBlock.name || "Content image"}
            className="content-block-renderer__image"
            onLoad={onLoad}
            onError={onError}
            themeConfig={themeConfig}
          />
        );

      case "video":
      case "external_video":
        return (
          <VideoContentBlock
            videoUrl={contentSource}
            className="content-block-renderer__video"
            onLoad={onLoad}
            onError={onError}
            onPlay={onPlay}
            onPause={onPause}
            onEnded={onEnded}
            themeConfig={themeConfig}
            loop={totalBlocks == 1}
          />
        );

      case "markdown":
        return (
          <MarkdownContentBlock
            markdownContent={contentSource}
            className="content-block-renderer__markdown"
            onRender={onLoad}
            onError={onError}
            themeConfig={themeConfig}
          />
        );

      case "html":
        return (
          <HTMLContentBlock
            htmlContent={contentSource}
            className="content-block-renderer__html"
            onRender={onLoad}
            onError={onError}
            themeConfig={themeConfig}
          />
        );

      case "iframe":
        return (
          <IFrameContentBlock
            src={contentSource}
            className="content-block-renderer__iframe"
            onLoad={onLoad}
            onError={onError}
          />
        );

      case "clock":
        return (
          <ContentBlockTypeClock
            className="content-block-renderer__clock"
            timeFormat={"12h"}
            showSeconds
            showDate
            locale="en-US"
            themeConfig={themeConfig}
          />
        );

      case "countdown": {
        if (!contentBlock.blockSettings) return null;
        try {
          const blockSettings = JSON.parse(contentBlock.blockSettings);
          return blockSettings && (
            <ContentBlockTypeCountDown
              className="content-block-renderer__countdown"
              targetDateTime={blockSettings.targetDateTime}
              completedText={blockSettings.completedText}
              title={blockSettings.title}
              confettiType={blockSettings.confettiType}
              loopConfetti={blockSettings.loopConfetti}
              themeConfig={themeConfig}
            />
          );
        } catch {
          return null;
        }
      }

      case "countup": {
        if (!contentBlock.blockSettings) return null;
        try {
          const blockSettings = JSON.parse(contentBlock.blockSettings);
          return blockSettings && (
            <ContentBlockTypeCountUp
              className="content-block-renderer__countup"
              startDateTime={blockSettings.startDateTime}
              headerText={blockSettings.headerText}
              footerText={blockSettings.footerText}
              themeConfig={themeConfig}
            />
          );
        } catch {
          return null;
        }
      }

      case "message":
        return (
          <ContentBlockTypeMessage
            className="content-block-renderer__message"
            message={contentBlock.contentData}
            themeConfig={themeConfig}
          />
        );

      case "calendar":
        return (
          <ContentBlockTypeCalendar
            className="content-block-renderer__clock"
            timezone={""}
            highlightToday
            showWeekNumbers
            weekStartsOn={1}
            themeConfig={themeConfig}
          />
        );

      case "unsplash_collection":
        return (
          <UnsplashCollectionContentBlock
            contentBlock={contentBlock}
            className="content-block-renderer__unsplash-collection"
            venue={venue}
            transitionType={transitionType}
            transitionDuration={transitionDuration}
            onLoad={onLoad}
            onError={onError}
            onEnded={onEnded}
            themeConfig={themeConfig}
          />
        );

      default:
        console.log("ContentBlockRenderer: Unsupported content block type for the block", contentBlock);
        return (
          <Box
            className="content-block-renderer__unsupported"
            h="100%"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "var(--mantine-spacing-lg)",
            }}
          >
            <Stack align="center" gap="md" maw={400}>
              <Alert color="orange" title="Unsupported Content Type" className="content-block-renderer__unsupported-alert">
                <Text size="sm" className="content-block-renderer__unsupported-message" ta="center">
                  Content type "{contentBlock.contentType}" is not supported.
                </Text>
              </Alert>
            </Stack>
          </Box>
        );
    }
  };

  return (
    <ContentBlockErrorBoundary
      className="content-block-renderer__error-boundary"
      onError={(error) => {
        console.error(`Error in content block ${contentBlock.id}:`, error);
        onError?.(`Error rendering ${contentBlock.contentType} content: ${error.message}`);
      }}
    >
      <Box
        className={`content-block-renderer content-block-renderer--${contentBlock.contentType} ${className || ""}`}
        h="100%"
        data-content-block-id={contentBlock.id}
        data-content-type={contentBlock.contentType}
      >
        {renderContentComponent()}
      </Box>
    </ContentBlockErrorBoundary>
  );
};